import {InputAdornment, TextField} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {useCallback, useState} from 'react';
import LocationInputDialog from './dialog';
import {
  LocationExtended,
  Location as LocationType,
  defaultLocation
} from '@/schemas/sub-schemas';
import {FormikErrors, FormikTouched} from 'formik';
import {FilterDocumentType} from '@/types/filter';
import FormLabel from '@/components/ui/form-label';

type ErrorType =
  | string
  | FormikErrors<{lat: number; lng: number; label: string}>
  | undefined;

export type LocationInputProps = {
  name: string;
  label: string;
  value?: LocationType | LocationExtended | null;
  onChange?: (newLocation: LocationType | LocationExtended | null) => void;
  error?: ErrorType;
  touched?: FormikTouched<{lat: number; lng: number; label: string}> | boolean;
  onBlur?: () => void;
  locationExtended?: boolean;
  showOnlyCity?: boolean;
  initialLocation?: LocationType | LocationExtended | null;
  showCircle?: boolean;
  allowClear?: boolean;
  height?: string;
  filterType?: FilterDocumentType;
  optional?: boolean;
};

export const LocationInput = ({
  name,
  label,
  value = {
    lat: 0,
    lng: 0,
    label: 'null'
  },
  onChange = () => {
    // noop
  },
  error,
  touched,
  onBlur = () => {
    /* noop */
  },
  showOnlyCity = false,
  locationExtended = false,
  initialLocation,
  showCircle,
  allowClear,
  height,
  filterType,
  optional
}: LocationInputProps) => {
  const [showPicker, setShowPicker] = useState<boolean>(false);

  const errorLabel = typeof error === 'string' ? error : error?.label;

  const handleOpenPicker = useCallback(() => {
    setShowPicker(true);
  }, []);
  const handleClosePicker = useCallback(() => {
    setShowPicker(false);
    onBlur();
  }, [onBlur]);

  const handleLocationChange = useCallback(
    (newLocation: LocationType | LocationExtended | null) => {
      if (newLocation === null && allowClear) {
        onChange(null);
      } else if (newLocation) {
        if (locationExtended) {
          const extendedLocation: LocationExtended = {
            ...newLocation,
            distance:
              filterType === 'event'
                ? (newLocation as LocationExtended).distance / 1000 || 15
                : (newLocation as LocationExtended).distance || 15
          };
          onChange(extendedLocation);
        } else {
          onChange(newLocation);
        }
      }
    },
    [allowClear, filterType, locationExtended, onChange]
  );

  return (
    <>
      <TextField
        name={name}
        label={<FormLabel text={label} required={!optional} />}
        value={value?.label || ''}
        error={touched && error ? true : false}
        helperText={touched && error ? errorLabel : undefined}
        fullWidth
        onClick={handleOpenPicker}
        InputProps={{
          startAdornment: (
            <InputAdornment position="end">
              <LocationOnIcon />
            </InputAdornment>
          )
        }}
      />
      <LocationInputDialog
        open={showPicker}
        onClose={handleClosePicker}
        initialLocation={initialLocation || defaultLocation}
        onChange={handleLocationChange}
        onlyCity={showOnlyCity}
        showCircle={showCircle}
        allowClear={allowClear}
        height={height}
        locationExtended={locationExtended}
      />
    </>
  );
};
